import { useEffect } from "react";
import { useNavigate } from "fusion-plugin-react-router";
import { HotjarEventNames } from "../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../shared/utils/trackHotjarEvent";
import Navbar from "../shared/components/navbar";
import Footer from "../shared/components/footer";
import PageContent from "../shared/components/PageContent";
import QuoteStepLoader from "../shared/components/QuoteStepLoader";
import { loginWithUber } from "../shared/services/insuranceService";
import {
  UBER_AUTH_CLIENT_ID,
  UBER_AUTH_RESPONSE_TYPE,
  UBER_AUTH_REDIRECT_PATH,
} from "../config/config";

const redirectToUberQuoteAuthorization = () => {
  const authorizationUrl = new URL(
    "https://login.uber.com/oauth/v2/authorize",
  );
  authorizationUrl.searchParams.set("client_id", UBER_AUTH_CLIENT_ID);
  authorizationUrl.searchParams.set("response_type", UBER_AUTH_RESPONSE_TYPE);
  authorizationUrl.searchParams.set(
    "redirect_uri",
    `${window.location.origin}${UBER_AUTH_REDIRECT_PATH}`,
  );
  window.location.replace(authorizationUrl.toString());
};

const UberAuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.get("error") === "access_denied") {
      navigate("/blocked-action?type=UberAccessDenied");
      return;
    }

    const code = queryParams.get("code");
    if (!code) {
      return;
    }

    sessionStorage.setItem("uberCode", code);

    const fetchLoginDetails = async () => {
      const requestBody = {
        portalLoginProvider: "Uber",
        portalLoginInputs: {
          UberAuthorizationCode: code,
          UberRedirectUrl: `${window.location.origin}/login/uber`,
        },
      };
      TrackHotjarEvent(HotjarEventNames.PORTAL_LOGIN_REQUEST);

      try {
        if (code.length > 32) {
          const responseData = await loginWithUber(requestBody);
          sessionStorage.setItem("userSession", JSON.stringify(responseData));
          const indicator = responseData.clientLandingPageIndicator;
          // Handle new customer blocking logic
          if (indicator === "NewCustomer") {
            if (responseData.blockedCustomer) {
              // Blocked new customer, redirect to BlockedAction with details
              const params = new URLSearchParams({
                type: "blocked-customer",
                blockedCategory: responseData.blockedCategory || "",
                blockedHeader: responseData.blockedHeader || "",
                blockedDescription: responseData.blockedDescription || "",
              });
              navigate(`/blocked-action?${params.toString()}`);
              return;
            }
            TrackHotjarEvent(
              HotjarEventNames.PORTAL_LOGIN_RESPONSE_NEW_CUSTOMER,
            );
            redirectToUberQuoteAuthorization();
          } else {
            TrackHotjarEvent(
              HotjarEventNames.PORTAL_LOGIN_RESPONSE_EXISTING_CUSTOMER,
            );
            navigate("/landing");
          }
        } else {
          throw new Error("Invalid Uber authorization code.");
        }
      } catch (error) {
        console.error(error);
        if (error.message && error.message.includes("404")) {
          navigate("/blocked-action?type=no-account");
        } else if (error.message && error.message.includes("400")) {
          navigate("/blocked-action?type=almost-there");
        } else {
          navigate(
            `/error?message=${encodeURIComponent(
              "We are unable to log you in using Uber authentication."
            )}`
          );
        }
      }
    };

    fetchLoginDetails();
  }, [navigate]);

  return (
    <>
      <Navbar />
      <PageContent>
        <QuoteStepLoader
          title="Logging you in"
          description="We're securely connecting your Uber account and preparing your portal."
          message="Signing you in..."
        />
      </PageContent>
      <Footer />
    </>
  );
};

export default UberAuthCallback;
